// components/user/lead/sell-device/AddressForm.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Select,
  Input,
  Radio,
  Spin,
  Divider,
  Drawer,
  Modal,
  Dropdown,
  Switch,
} from "antd";
import {
  FiMapPin,
  FiHome,
  FiBriefcase,
  FiSmartphone,
  FiEdit2,
  FiCheckCircle,
  FiMap,
  FiTrash2,
  FiMoreVertical,
  FiPlus,
  FiStar,
} from "react-icons/fi";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { IoMdPin } from "react-icons/io";
import { CloseOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserAddresses,
  saveAddress,
  editAddress,
  deleteAddress,
  setSelectedAddress,
} from "../../../../store/slices/sellDeviceSlice";
import toast from "react-hot-toast";
import { api } from "../../../../services/api";
import { pincodeLookupRejection, unavailablePincode, validPincodeLookup } from "../../../../utils/pincodeAvailability";

const AddressForm = ({
  formik,
  isDarkMode,
  onAddressSaved,
  onUnavailable,
  categoryId,
  brandId,
}) => {
  const dispatch = useDispatch();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [editMode, setEditMode] = useState(false);
  const [editAddressId, setEditAddressId] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [drawerPincodeData, setDrawerPincodeData] = useState(null);
  const [pincodeLookupLoading, setPincodeLookupLoading] = useState(false);
  const [pincodeLookupError, setPincodeLookupError] = useState("");
  const [validatingAddressId, setValidatingAddressId] = useState(null);
  const selectionLookupRef = useRef(0);
  const pincodeLookupRef = useRef(0);

  const isSyncingRef = useRef(false);

  const {
    addresses,
    addressesLoading,
    selectedAddressId,
    savingAddress,
    editingAddress,
    deletingAddress,
  } = useSelector((state) => state.sellDevice);

  const [localAddress, setLocalAddress] = useState({
    pincode: "",
    areaId: null,
    areaName: "",
    cityName: "",
    cityId: null,
    flatNo: "",
    street: "",
    addressType: "Home",
    isDefault: false,
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load addresses on mount
  useEffect(() => {
    dispatch(getUserAddresses());
  }, [dispatch]);

  // Sync selected address to formik
  useEffect(() => {
    if (
      selectedAddressId !== undefined &&
      selectedAddressId !== null &&
      !isSyncingRef.current
    ) {
      isSyncingRef.current = true;
      if (formik.values.addressId !== selectedAddressId) {
        formik.setFieldValue("addressId", selectedAddressId);
      }
      isSyncingRef.current = false;
    }
  }, [selectedAddressId, formik.values.addressId, formik]);

  const openDrawer = (address = null) => {
    // Ignore results of old pincode lookups when switching addresses.
    pincodeLookupRef.current += 1;
    selectionLookupRef.current += 1;
    setDrawerPincodeData(null);
    setPincodeLookupLoading(false);
    setPincodeLookupError("");
    if (address) {
      setEditMode(true);
      setEditAddressId(address.id);
      setLocalAddress({
        pincode: address.pincode || "",
        areaId: address.area_id || null,
        areaName: address.area_name || "",
        cityName: address.city_name || "",
        cityId: address.city_id || null,
        flatNo: address.flat_no || "",
        street: address.street || "",
        addressType: address.address_type || "Home",
        isDefault: address.is_default || false,
      });
    } else {
      setEditMode(false);
      setEditAddressId(null);
      setLocalAddress({
        pincode: "",
        areaId: null,
        areaName: "",
        cityName: "",
        cityId: null,
        flatNo: "",
        street: "",
        addressType: "Home",
        isDefault: false,
      });
    }
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    pincodeLookupRef.current += 1;
    selectionLookupRef.current += 1;
    setDrawerPincodeData(null);
    setPincodeLookupLoading(false);
    setPincodeLookupError("");
    setDrawerVisible(false);
    setEditMode(false);
    setEditAddressId(null);
    setLocalAddress({
      pincode: "",
      areaId: null,
      areaName: "",
      cityName: "",
      cityId: null,
      flatNo: "",
      street: "",
      addressType: "Home",
      isDefault: false,
    });
  };

  const handleSaveAddress = async () => {
    if (isSubmitting || pincodeLookupLoading) return;

    // These columns are required in MySQL. Never send city_id: null when
    // editing an address before (or without) a new pincode lookup.
    if (!/^\d{6}$/.test(localAddress.pincode) || !localAddress.cityId || !localAddress.areaId) {
      toast.error("Enter a valid pincode and select the area before saving.");
      return;
    }
    if (!localAddress.flatNo.trim()) {
      toast.error("Enter a flat or house number.");
      return;
    }
    setIsSubmitting(true);

    const addressData = {
      area_id: localAddress.areaId,
      city_id: localAddress.cityId,
      street: localAddress.street,
      flat_no: localAddress.flatNo.trim(),
      pincode: localAddress.pincode,
      address_type: localAddress.addressType,
      is_default: localAddress.isDefault,
    };

    try {
      let result;
      if (editMode) {
        result = await dispatch(
          editAddress({ addressId: editAddressId, addressData }),
        );
      } else {
        result = await dispatch(saveAddress(addressData));
      }

      if (
        saveAddress.fulfilled.match(result) ||
        editAddress.fulfilled.match(result)
      ) {
        toast.success(
          editMode
            ? "Address updated successfully"
            : "Address added successfully",
        );

        // Close drawer first
        closeDrawer();

        // Then refresh addresses
        await dispatch(getUserAddresses());

        // Select the new/updated address
        const addressId = editMode ? editAddressId : result.payload?.id;
        if (addressId) {
          dispatch(setSelectedAddress(addressId));
          if (onAddressSaved) {
            await onAddressSaved(addressId, addressData.pincode);
          }
        }
      } else {
        toast.error(result.payload || "Failed to save address");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetDefault = async (address) => {
    // Default-only updates must not rewrite location foreign keys.
    const addressData = { is_default: true };

    const result = await dispatch(
      editAddress({ addressId: address.id, addressData }),
    );

    if (editAddress.fulfilled.match(result)) {
      toast.success("Default address updated");
      await dispatch(getUserAddresses());
      await handleSelectAddress(address);
    } else {
      toast.error(result.payload || "Failed to update default address");
    }
  };

  const handleDeleteAddress = async () => {
    if (addressToDelete) {
      const result = await dispatch(deleteAddress(addressToDelete.id));
      if (deleteAddress.fulfilled.match(result)) {
        toast.success("Address deleted successfully");
        if (selectedAddressId === addressToDelete.id) {
          dispatch(setSelectedAddress(null));
        }
        setDeleteModalVisible(false);
        setAddressToDelete(null);
        await dispatch(getUserAddresses());
      } else {
        toast.error(result.payload || "Failed to delete address");
      }
    }
  };

  const handleSelectAddress = async (address) => {
    const requestId = ++selectionLookupRef.current;
    dispatch(setSelectedAddress(null));
    formik.setFieldValue("addressId", null);
    setValidatingAddressId(address.id);
    try {
      const result = await api.getPincodeAddress(address.pincode, categoryId, brandId);
      if (requestId !== selectionLookupRef.current) return;
      if (!validPincodeLookup(result, address.pincode)) {
        onUnavailable(unavailablePincode(address.pincode));
        return;
      }
      dispatch(setSelectedAddress(address.id));
      formik.setFieldValue("addressId", address.id);
    } catch (error) {
      if (requestId !== selectionLookupRef.current) return;
      const unavailable = pincodeLookupRejection(error, address.pincode);
      if (unavailable) onUnavailable(unavailable);
      else toast.error("Unable to check this address right now. Please try again.");
    } finally {
      if (requestId === selectionLookupRef.current) setValidatingAddressId(null);
    }
  };

  const getAddressTypeIcon = (type) => {
    switch (type) {
      case "Home":
        return <FiHome size={16} />;
      case "Office":
        return <FiBriefcase size={16} />;
      default:
        return <FiMap size={16} />;
    }
  };

  const handlePincodeChangeInDrawer = async (value) => {
    const requestId = ++pincodeLookupRef.current;
    setDrawerPincodeData(null);
    setPincodeLookupError("");
    setPincodeLookupLoading(value.length === 6);
    setLocalAddress((previous) => ({
      ...previous,
      pincode: value,
      areaId: null,
      areaName: "",
      cityId: null,
      cityName: "",
    }));

    if (value.length !== 6) return;

    try {
      const response = await api.getPincodeAddress(value, categoryId, brandId);
      if (requestId !== pincodeLookupRef.current) return;
      const result = response?.data ?? response;
      if (!validPincodeLookup(response, value)) {
        closeDrawer();
        onUnavailable(unavailablePincode(value));
        return;
      }
      setDrawerPincodeData(result);
      setLocalAddress((previous) => previous.pincode === value ? ({
        ...previous,
        cityId: result.city.id,
        cityName: result.city.name || "",
      }) : previous);
    } catch (error) {
      if (requestId === pincodeLookupRef.current) {
        const unavailable = pincodeLookupRejection(error, value);
        if (unavailable) {
          closeDrawer();
          onUnavailable(unavailable);
        } else {
          setPincodeLookupError("Unable to check this PIN code right now. Please try again.");
        }
      }
    } finally {
      if (requestId === pincodeLookupRef.current) setPincodeLookupLoading(false);
    }
  };

  const getDisplayCityName = () => localAddress.cityName || "";
  const drawerAreas = drawerPincodeData?.areas || [];
  const originalAddress = editMode
    ? addresses.find((address) => address.id === editAddressId)
    : null;
  const hasUnchangedArea = Boolean(originalAddress &&
    originalAddress.pincode === localAddress.pincode && localAddress.areaId);

  const isLoading = savingAddress || editingAddress || isSubmitting || pincodeLookupLoading || validatingAddressId !== null;

  return (
    <>
      <Divider
        className={`my-2 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
      />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2
            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
          >
            <FiMapPin size={18} className="text-green-500" />
            Pickup Address
          </h2>
          <button
            onClick={() => openDrawer()}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-all ${
              isDarkMode
                ? "text-blue-400 hover:bg-gray-700"
                : "text-blue-600 hover:bg-blue-50"
            }`}
          >
            <FiPlus size={14} />
            <span>Add New Address</span>
          </button>
        </div>

        {/* Saved Addresses List */}
        {addressesLoading ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : addresses.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {addresses.map((address) => (
              <div
                key={address.id}
                className={`rounded-lg border p-4 cursor-pointer transition-all ${
                  selectedAddressId === address.id
                    ? isDarkMode
                      ? "bg-green-900/20 border-green-500"
                      : "bg-green-50 border-green-500"
                    : isDarkMode
                      ? "bg-gray-700/30 border-gray-600 hover:border-gray-500"
                      : "bg-gray-50 border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => handleSelectAddress(address)}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`p-2 rounded-lg ${isDarkMode ? "bg-green-900/20" : "bg-green-50"}`}
                  >
                    <FiMapPin className="text-green-500" size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <div
                          className={`flex items-center gap-1 text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {getAddressTypeIcon(address.address_type)}
                          <span>{address.address_type}</span>
                        </div>
                        <div
                          className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                            isDarkMode
                              ? "bg-green-900/20 text-green-400"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          <IoMdPin size={10} />
                          <span>{address.pincode}</span>
                        </div>
                        {address.is_default && (
                          <div
                            className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                              isDarkMode
                                ? "bg-yellow-900/20 text-yellow-400"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            <FiStar size={10} />
                            <span>Default</span>
                          </div>
                        )}
                      </div>

                      <Dropdown
                        trigger={["click"]}
                        placement="bottomRight"
                        menu={{
                          items: [
                            {
                              key: "edit",
                              label: "Edit",
                              icon: <FiEdit2 />,
                              onClick: () => openDrawer(address),
                            },
                            ...(!address.is_default
                              ? [
                                  {
                                    key: "setDefault",
                                    label: "Set as Default",
                                    icon: <FiStar />,
                                    onClick: () => handleSetDefault(address),
                                  },
                                ]
                              : []),
                            {
                              key: "delete",
                              label: "Delete",
                              icon: <FiTrash2 />,
                              danger: true,
                              onClick: () => {
                                setAddressToDelete(address);
                                setDeleteModalVisible(true);
                              },
                            },
                          ],
                        }}
                      >
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className={`p-1 rounded-lg transition-colors ${
                            isDarkMode
                              ? "hover:bg-gray-600"
                              : "hover:bg-gray-200"
                          }`}
                        >
                          <FiMoreVertical size={16} />
                        </button>
                      </Dropdown>
                    </div>

                    <p
                      className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                    >
                      {address.flat_no && `${address.flat_no}, `}
                      {address.street && `${address.street}, `}
                    </p>

                    <p
                      className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                    >
                      {address.area_name && `${address.area_name}, `}
                      {address.city_name}
                    </p>

                    <p
                      className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {address.pincode}
                    </p>

                    {validatingAddressId === address.id && (
                      <p className="mt-2 text-xs text-gray-500">Checking pickup availability...</p>
                    )}
                    {selectedAddressId === address.id && (
                      <div
                        className={`mt-2 pt-2 border-t flex items-center gap-1 ${
                          isDarkMode
                            ? "border-gray-600 text-green-400"
                            : "border-gray-200 text-green-600"
                        }`}
                      >
                        <FiCheckCircle size={12} />
                        <span className="text-xs">Selected for pickup</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            onClick={() => openDrawer()}
            className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-all ${
              isDarkMode
                ? "border-gray-600 hover:border-gray-500 bg-gray-800/50"
                : "border-gray-300 hover:border-gray-400 bg-gray-50"
            }`}
          >
            <HiOutlineLocationMarker
              className={`mx-auto text-3xl mb-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            />
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              No addresses saved yet
            </p>
            <p
              className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              Click to add your first address
            </p>
          </div>
        )}
      </div>

      {/* Address Form Drawer */}
      <Drawer
        title={editMode ? "Edit Address" : "Add New Address"}
        placement={isMobile ? "bottom" : "right"}
        onClose={closeDrawer}
        open={drawerVisible}
        width={isMobile ? undefined : 480}
        height={isMobile ? "auto" : undefined}
        className={isDarkMode ? "dark-drawer" : ""}
        closeIcon={<CloseOutlined className={isDarkMode ? "text-white" : ""} />}
        headerStyle={{
          borderBottom: `1px solid ${isDarkMode ? "#374151" : "#f0f0f0"}`,
          background: isDarkMode ? "#1f2937" : "#fff",
          color: isDarkMode ? "#fff" : "#000",
        }}
        bodyStyle={{
          background: isDarkMode ? "#1f2937" : "#fff",
          padding: "20px",
        }}
        destroyOnClose={true}
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label
                  className={`block text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                >
                  Pincode *
                </label>
                <Input
                  placeholder="Enter 6-digit pincode"
                  maxLength={6}
                  value={localAddress.pincode}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value && !/^\d*$/.test(value)) return;
                    handlePincodeChangeInDrawer(value);
                  }}
                  className="rounded-lg"
                  prefix={
                    pincodeLookupLoading ? (
                      <Spin size="small" />
                    ) : (
                      <FiMapPin className="text-gray-400" />
                    )
                  }
                />
              </div>

              <div>
                <label
                  className={`block text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                >
                  City
                </label>
                <Input
                  value={getDisplayCityName()}
                  placeholder={
                    pincodeLookupLoading ? "Loading..." : "City will auto-fill"
                  }
                  disabled
                  className="bg-gray-100 dark:bg-gray-700 rounded-lg"
                  prefix={<FiSmartphone size={14} className="text-gray-400" />}
                />
              </div>
            </div>

            {pincodeLookupError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3" role="alert">
                <p className="text-sm font-medium text-red-700">{pincodeLookupError}</p>
              </div>
            )}

            <div>
              <label
                className={`block text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                Area/Locality *
              </label>
              {hasUnchangedArea && localAddress.areaName ? (
                <Input
                  value={localAddress.areaName}
                  disabled
                  className="bg-gray-100 dark:bg-gray-700 rounded-lg"
                  prefix={<FiMap size={14} className="text-gray-400" />}
                />
              ) : (
                <Select
                  placeholder="Select area"
                  className="w-full rounded-lg"
                  value={localAddress.areaId}
                  onChange={(value) =>
                    setLocalAddress({
                      ...localAddress,
                      areaId: value,
                      areaName: drawerAreas.find((area) => area.id === value)?.area || "",
                    })
                  }
                  disabled={!drawerPincodeData || pincodeLookupLoading}
                  loading={pincodeLookupLoading}
                  options={drawerAreas.map((area) => ({
                    value: area.id,
                    label: area.area,
                  }))}
                  notFoundContent={
                    drawerPincodeData ? "No areas found" : "Enter pincode first"
                  }
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label
                className={`block text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                Flat/House No. *
              </label>
              <Input
                placeholder="Enter flat/house number"
                value={localAddress.flatNo}
                onChange={(e) =>
                  setLocalAddress({ ...localAddress, flatNo: e.target.value })
                }
                className="rounded-lg"
                prefix={<FiHome size={14} className="text-gray-400" />}
              />
            </div>

            <div>
              <label
                className={`block text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                Street Address (optional)
              </label>
              <Input
                placeholder="Enter street address"
                value={localAddress.street}
                onChange={(e) =>
                  setLocalAddress({ ...localAddress, street: e.target.value })
                }
                className="rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="flex items-center justify-between">
              <label
                className={`text-xs font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                Set as Default Address
              </label>
              <Switch
                checked={localAddress.isDefault}
                onChange={(checked) =>
                  setLocalAddress({ ...localAddress, isDefault: checked })
                }
              />
            </div>

            <div>
              <label
                className={`block text-xs font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                Address Type
              </label>
              <Radio.Group
                value={localAddress.addressType}
                onChange={(e) =>
                  setLocalAddress({
                    ...localAddress,
                    addressType: e.target.value,
                  })
                }
              >
                <Radio value="Home">
                  <div className="flex items-center gap-1">
                    <FiHome size={14} />
                    <span>Home</span>
                  </div>
                </Radio>
                <Radio value="Office">
                  <div className="flex items-center gap-1">
                    <FiBriefcase size={14} />
                    <span>Office</span>
                  </div>
                </Radio>
                <Radio value="Other">
                  <div className="flex items-center gap-1">
                    <FiMap size={14} />
                    <span>Other</span>
                  </div>
                </Radio>
              </Radio.Group>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={closeDrawer}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                isDarkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveAddress}
              disabled={
                !/^\d{6}$/.test(localAddress.pincode) ||
                !localAddress.cityId ||
                !localAddress.areaId ||
                !localAddress.flatNo.trim() ||
                isLoading
              }
              className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all ${
                !/^\d{6}$/.test(localAddress.pincode) ||
                !localAddress.cityId ||
                !localAddress.areaId ||
                !localAddress.flatNo.trim() ||
                isLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {isLoading ? (
                <Spin size="small" />
              ) : editMode ? (
                "Update Address"
              ) : (
                "Save Address"
              )}
            </button>
          </div>
        </div>
      </Drawer>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Address"
        open={deleteModalVisible}
        onOk={handleDeleteAddress}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: deletingAddress }}
        className={isDarkMode ? "dark-modal" : ""}
      >
        <p>Are you sure you want to delete this address?</p>
        <p className="mt-2 text-sm text-gray-500">
          This action cannot be undone.
        </p>
      </Modal>
    </>
  );
};

export default AddressForm;
